// src/utils/reputation.ts

export type ReputationTrackKey =
  | 'species_mastery'
  | 'explorer'
  | 'community_contributor'
  | 'bazaar_trader'
  | 'tournament_competitor';

export type ReputationTier =
  | 'none'
  | 'bronze'
  | 'silver'
  | 'gold'
  | 'platinum'
  | 'mythic';

export interface ReputationTrackThresholds {
  bronze: number;
  silver: number;
  gold: number;
  platinum: number;
  mythic: number;
}

export interface ReputationTrackState {
  key: ReputationTrackKey;
  value: number;
  tier: ReputationTier;
  nextTier: ReputationTier | null;
  nextThreshold: number | null;
  progressWithinTierPct: number;
}

export interface ReputationSummary {
  schemaVersion: number;
  tracks: Record<ReputationTrackKey, ReputationTrackState>;
  featuredBadgeKeys: string[];
  primaryBadgeKey: string | null;
  updatedAt?: number;
}

export interface BadgeDisplay {
  key: string;
  track: ReputationTrackKey;
  tier: ReputationTier;
  icon: string;
  label: string;
  shortLabel: string;
  colorClass: string;
  order: number;
}

export const REPUTATION_SCHEMA_VERSION = 2;

export const REPUTATION_THRESHOLDS: Record<ReputationTrackKey, ReputationTrackThresholds> = {
  // Reputation is trust/community value, not raw XP.
  // These tracks should move slower than level and should favor breadth/quality.
  species_mastery: {
    bronze: 5,
    silver: 15,
    gold: 40,
    platinum: 100,
    mythic: 200,
  },
  explorer: {
    bronze: 5,
    silver: 25,
    gold: 100,
    platinum: 250,
    mythic: 500,
  },
  community_contributor: {
    bronze: 5,
    silver: 25,
    gold: 100,
    platinum: 250,
    mythic: 500,
  },
  bazaar_trader: {
    bronze: 5,
    silver: 25,
    gold: 100,
    platinum: 250,
    mythic: 500,
  },
  tournament_competitor: {
    bronze: 3,
    silver: 10,
    gold: 30,
    platinum: 75,
    mythic: 150,
  },
};

export const TRACK_LABELS: Record<ReputationTrackKey, string> = {
  species_mastery: 'Species Mastery',
  explorer: 'Explorer',
  community_contributor: 'Community Contributor',
  bazaar_trader: 'Bazaar Trader',
  tournament_competitor: 'Tournament Competitor',
};

export const TRACK_PRIORITY: Record<ReputationTrackKey, number> = {
  // Keep tie-breaking identical to the server's featured badge ordering.
  species_mastery: 5,
  explorer: 4,
  community_contributor: 3,
  bazaar_trader: 2,
  tournament_competitor: 1,
};

export const TRACK_ICONS: Record<ReputationTrackKey, string> = {
  species_mastery: '🐟',
  explorer: '🧭',
  community_contributor: '💬',
  bazaar_trader: '🛒',
  tournament_competitor: '🏆',
};

export const TIER_LABELS: Record<ReputationTier, string> = {
  none: 'None',
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
  mythic: 'Mythic',
};

export const TIER_ORDER: Record<ReputationTier, number> = {
  none: 0,
  bronze: 1,
  silver: 2,
  gold: 3,
  platinum: 4,
  mythic: 5,
};

export function getTierForValue(
  value: number,
  thresholds: ReputationTrackThresholds,
): ReputationTier {
  const n = Number.isFinite(Number(value)) ? Number(value) : 0;
  if (n >= thresholds.mythic) return 'mythic';
  if (n >= thresholds.platinum) return 'platinum';
  if (n >= thresholds.gold) return 'gold';
  if (n >= thresholds.silver) return 'silver';
  if (n >= thresholds.bronze) return 'bronze';
  return 'none';
}

export function getNextTier(tier: ReputationTier): ReputationTier | null {
  if (tier === 'none') return 'bronze';
  if (tier === 'bronze') return 'silver';
  if (tier === 'silver') return 'gold';
  if (tier === 'gold') return 'platinum';
  if (tier === 'platinum') return 'mythic';
  return null;
}

export function getThresholdForTier(
  tier: ReputationTier,
  thresholds: ReputationTrackThresholds,
): number | null {
  if (tier === 'bronze') return thresholds.bronze;
  if (tier === 'silver') return thresholds.silver;
  if (tier === 'gold') return thresholds.gold;
  if (tier === 'platinum') return thresholds.platinum;
  if (tier === 'mythic') return thresholds.mythic;
  return null;
}

export function computeTrackState(
  key: ReputationTrackKey,
  value: number,
): ReputationTrackState {
  const thresholds = REPUTATION_THRESHOLDS[key];
  const safeValue = Number.isFinite(Number(value)) ? Number(value) : 0;
  const tier = getTierForValue(safeValue, thresholds);
  const nextTier = getNextTier(tier);
  const nextThreshold = nextTier ? getThresholdForTier(nextTier, thresholds) : null;

  let floor = 0;
  if (tier === 'bronze') floor = thresholds.bronze;
  if (tier === 'silver') floor = thresholds.silver;
  if (tier === 'gold') floor = thresholds.gold;
  if (tier === 'platinum') floor = thresholds.platinum;
  if (tier === 'mythic') floor = thresholds.mythic;

  const ceiling =
    tier === 'none'
      ? thresholds.bronze
      : tier === 'bronze'
        ? thresholds.silver
        : tier === 'silver'
          ? thresholds.gold
          : tier === 'gold'
            ? thresholds.platinum
            : tier === 'platinum'
              ? thresholds.mythic
              : thresholds.mythic;

  const span = Math.max(1, ceiling - floor);
  const progressWithinTierPct =
    tier === 'mythic'
      ? 100
      : Math.max(0, Math.min(100, ((safeValue - floor) / span) * 100));

  return {
    key,
    value: safeValue,
    tier,
    nextTier,
    nextThreshold,
    progressWithinTierPct,
  };
}

export function makeBadgeKey(track: ReputationTrackKey, tier: ReputationTier): string {
  return `${track}:${tier}`;
}

export function getBadgeDisplay(track: ReputationTrackKey, tier: ReputationTier): BadgeDisplay | null {
  if (tier === 'none') return null;

  const key = makeBadgeKey(track, tier);
  const icon = TRACK_ICONS[track];
  const label = `${TRACK_LABELS[track]} — ${TIER_LABELS[tier]}`;
  const shortLabel = `${icon} ${TIER_LABELS[tier]}`;

  const colorClass =
    tier === 'bronze'
      ? 'bg-amber-100 text-amber-900 border-amber-300'
      : tier === 'silver'
        ? 'bg-slate-100 text-slate-900 border-slate-300'
        : tier === 'gold'
          ? 'bg-yellow-100 text-yellow-900 border-yellow-300'
          : tier === 'platinum'
            ? 'bg-cyan-100 text-cyan-900 border-cyan-300'
            : 'bg-fuchsia-100 text-fuchsia-900 border-fuchsia-300';

  return {
    key,
    track,
    tier,
    icon,
    label,
    shortLabel,
    colorClass,
    order: TIER_ORDER[tier] * 100 + TRACK_PRIORITY[track],
  };
}

export function buildReputationSummary(input?: Partial<Record<ReputationTrackKey, number>>): ReputationSummary {
  const values: Record<ReputationTrackKey, number> = {
    species_mastery: Number(input?.species_mastery ?? 0),
    explorer: Number(input?.explorer ?? 0),
    community_contributor: Number(input?.community_contributor ?? 0),
    bazaar_trader: Number(input?.bazaar_trader ?? 0),
    tournament_competitor: Number(input?.tournament_competitor ?? 0),
  };

  const tracks: Record<ReputationTrackKey, ReputationTrackState> = {
    species_mastery: computeTrackState('species_mastery', values.species_mastery),
    explorer: computeTrackState('explorer', values.explorer),
    community_contributor: computeTrackState('community_contributor', values.community_contributor),
    bazaar_trader: computeTrackState('bazaar_trader', values.bazaar_trader),
    tournament_competitor: computeTrackState('tournament_competitor', values.tournament_competitor),
  };

  const featuredBadgeKeys = Object.values(tracks)
    .map((track) => getBadgeDisplay(track.key, track.tier))
    .filter(Boolean)
    .sort((a, b) => (b!.order - a!.order))
    .slice(0, 3)
    .map((b) => b!.key);

  return {
    schemaVersion: REPUTATION_SCHEMA_VERSION,
    tracks,
    featuredBadgeKeys,
    primaryBadgeKey: featuredBadgeKeys[0] ?? null,
    updatedAt: Date.now(),
  };
}

export function extractTrackValuesFromSummary(summary: any): Record<ReputationTrackKey, number> {
  return {
    species_mastery: Number(summary?.tracks?.species_mastery?.value ?? 0),
    explorer: Number(summary?.tracks?.explorer?.value ?? 0),
    community_contributor: Number(summary?.tracks?.community_contributor?.value ?? 0),
    bazaar_trader: Number(summary?.tracks?.bazaar_trader?.value ?? 0),
    tournament_competitor: Number(summary?.tracks?.tournament_competitor?.value ?? 0),
  };
}

export function getFeaturedBadges(summary: any): BadgeDisplay[] {
  if (!summary?.tracks) return [];

  return Object.values(summary.tracks as Record<ReputationTrackKey, ReputationTrackState>)
    .map((track) => getBadgeDisplay(track.key, track.tier))
    .filter(Boolean)
    .sort((a, b) => (b!.order - a!.order))
    .slice(0, 3) as BadgeDisplay[];
}